import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';
import { Module, Extension, ModuleContextType, ModuleDefinition } from '../types/module';
import { api } from '../services/api';
import { useAuth } from './AuthContext';

const ModuleLoaderContext = createContext<ModuleContextType | undefined>(undefined);

export const useModuleLoader = () => {
    const context = useContext(ModuleLoaderContext);
    if (!context) {
        throw new Error('useModuleLoader must be used within a ModuleLoaderProvider');
    }
    return context;
};

interface ModuleLoaderProviderProps {
    children: ReactNode;
    initialModules?: Module[];
}

export const ModuleLoaderProvider: React.FC<ModuleLoaderProviderProps> = ({ children, initialModules = [] }) => {
    const { user } = useAuth();
    const [modules, setModules] = useState<Module[]>([]);
    const [extensions, setExtensions] = useState<Extension[]>([]);
    const [activeModules, setActiveModules] = useState<ModuleDefinition[]>([]);
    const [initialized, setInitialized] = useState(false);

    const registerExtension = useCallback((extension: Extension) => {
        setExtensions(prev => {
            // Avoid duplicates
            if (prev.find(e => e.id === extension.id)) return prev;
            return [...prev, extension].sort((a, b) => (a.order || 0) - (b.order || 0));
        });
    }, []);

    const registerModule = useCallback((module: Module) => {
        setModules(prev => {
            if (prev.find(m => m.id === module.id)) return prev;
            return [...prev, module];
        });
    }, []);

    // Fetch active module configuration from backend
    useEffect(() => {
        const fetchModules = async () => {
            if (!user) {
                setActiveModules([]);
                return;
            }
            try {
                const data = await api.system.listModules();
                if (data.modules) {
                    setActiveModules(data.modules.filter(m => m.enabled));
                }
            } catch (error) {
                console.error("Failed to load module config:", error);
            }
        };
        fetchModules();
    }, [user]);

    // Create the context value object
    const contextValue: ModuleContextType = {
        registerModule,
        registerExtension,
        getExtensions: (slot: string) => extensions.filter(e => e.slot === slot),
        modules,
        activeModules
    };

    // Initialize initial modules once
    if (!initialized && initialModules.length > 0) {
        initialModules.forEach(module => {
            module.initialize(contextValue);
            registerModule(module);
        });
        setInitialized(true);
    }

    return (
        <ModuleLoaderContext.Provider value={contextValue}>
            {children}
        </ModuleLoaderContext.Provider>
    );
};
